import { Box, Button, FormControl, IconButton, InputAdornment, OutlinedInput, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertFillIcon, HidePassword, LockFillIcon, VectorIcon } from '~/assets/icons';
import { BASE_COLORS } from '~/utils/constants/common';
import styles from '../auth.module.scss';
import AuthLayout from '../AuthLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminRoute } from '~/utils/constants/route';
import { useFormik } from 'formik';
import authValidation from '~/validation/auth';
import { handleApiError, notify } from '~/utils/helper';
import authApi from '~/apis/auth';

type ResetPasswordType = {
  password: string;
  confirmPassword: string;
};

const cx = classNames.bind(styles);

const initialValues: ResetPasswordType = {
  password: '',
  confirmPassword: '',
};

const ResetPassword = () => {
  const { t } = useTranslation('admin/auth');

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const phone = searchParams.get('phone');

  const form = useFormik<ResetPasswordType>({
    initialValues,
    validationSchema: authValidation(t).resetPassword,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      const { password } = values;

      try {
        setSubmitting(true);
        await authApi.resetPassword({ phone, new_password: password });
        notify('success', t('resetPassword.resetPasswordSuccess'));
        navigate(`${adminRoute.base}${adminRoute.login}`);
      } catch (error) {
        if (!phone) {
          notify('error', t('validation.phoneInvalid'));
          return;
        }
        handleApiError(error);
        return;
      } finally {
        setSubmitting(false);
        return;
      }
    },
  });

  return (
    <AuthLayout>
      <Typography className='text-center font-bold text-[#ED1002] lg:text-[40px] xl:text-[44px]'>{`${t(
        'forgotPassword.title'
      ).toUpperCase()}`}</Typography>
      <Box>
        <Typography className='xl1440:text-[20px] whitespace-pre-line text-center'>
          {t('resetPassword.title')}
        </Typography>
        <Box className={cx('px-7 mt-8', 'input-custom')}>
          <Box className='flex justify-center'>
            <FormControl fullWidth>
              <OutlinedInput
                id='password'
                value={form.values.password}
                onChange={form.handleChange}
                type={showPassword ? 'text' : 'password'}
                placeholder={t('resetPassword.password')}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                      edge='end'
                    >
                      {showPassword ? <VectorIcon fill='#ED1002' /> : <HidePassword fill='#ED1002' />}
                    </IconButton>
                  </InputAdornment>
                }
                startAdornment={
                  <InputAdornment position='start'>
                    <LockFillIcon />
                  </InputAdornment>
                }
              />
              {form.touched.password && form.errors.password && (
                <Typography color='error' className='text-sm mt-2 flex items-end gap-[6px]'>
                  <AlertFillIcon fill={BASE_COLORS.RED} />
                  {form.errors.password}
                </Typography>
              )}
            </FormControl>
          </Box>
          <Box className='flex justify-center mt-5'>
            <FormControl fullWidth>
              <OutlinedInput
                id='confirmPassword'
                value={form.values.confirmPassword}
                onChange={form.handleChange}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('resetPassword.confirmPassword')}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => {
                        setShowConfirmPassword(!showConfirmPassword);
                      }}
                      edge='end'
                    >
                      {showConfirmPassword ? <VectorIcon fill='#ED1002' /> : <HidePassword fill='#ED1002' />}
                    </IconButton>
                  </InputAdornment>
                }
                startAdornment={
                  <InputAdornment position='start'>
                    <LockFillIcon />
                  </InputAdornment>
                }
              />
              {form.touched.confirmPassword && form.errors.confirmPassword && (
                <Typography color='error' className='text-sm mt-2 flex items-end gap-[6px]'>
                  <AlertFillIcon fill={BASE_COLORS.RED} />
                  {form.errors.confirmPassword}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Box className='flex justify-evenly mt-10'>
            <Button
              className={`shadow-[0px_4px_4px_0px_#00000040] text-[${BASE_COLORS.BLUE_BOLD}] lg:text-[16px] xl1440:text-[18px] px-6`}
              onClick={() => {
                navigate(`${adminRoute.base}${adminRoute.forgotPassword}`);
              }}
            >
              {t('forgotPassword.cancel')}
            </Button>
            <Button
              className={`shadow-[0px_4px_4px_0px_#00000040] bg-[#ED1002] text-white lg:text-[16px] xl1440:text-[18px] px-6 hover:bg-[#ED1002]`}
              onClick={() => form.handleSubmit()}
              disabled={form.isSubmitting}
            >
              {t('forgotPassword.changePassword')}
            </Button>
          </Box>
        </Box>
      </Box>
    </AuthLayout>
  );
};

export default ResetPassword;
